import React, { ChangeEvent, useEffect, useRef } from 'react';
import { IPaymentConnector, IPaymentState } from '~/interfaces/payment';
import Styles from './credit-card.module.scss';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import { IGetPricing } from '~/interfaces/commerce-types';
import {
  useIsLoading,
  useSelectedPaymentOption,
  useSetSelectedPaymentOption,
  useSetPaymentReview,
  useSetProgressiveDetails,
  useSetPaymentConnectorId,
  useSetCartId,
  useSyfOverlay,
  useBillingAddressState,
} from '~/store/wmap-store';
import CustomSkeleton from '~/components/custom-skeleton';
import {
  getBalanceErrorMessages,
  BILLING_ADDRESS_WARNING,
  MINIMUM_FINANCING_AMOUNT,
  TYPES,
} from '~/modules/payment/constants';
import { PaymentOptions } from '~/modules/payment/components/payments-options';
import PaymentSelector from '~/modules/payment/components/payment-selector';
import {
  AffirmConfiguration,
  ProgressiveConfiguration,
  SynchronyConfiguration,
  Maybe,
  UsAePayErrorMessage,
  FeatureFlag,
} from '~/gql/__generated__/types';
import PaymentBalanceError from './payment-balance-error';
import {
  handleAdyenRadioTealium,
  handleAffirmRadioTealium,
  handleCreditCardRadioTealium,
  handleProgressiveRadioTealium,
  handleSynchronyRadioTealium,
} from '~/utils/tealium/tealium-utag-link-events';
import { isBillingFormInValid } from '~/components/billing-address/components/utils';
import { formatCurrency } from '~/utils/utils';

interface PaymentsContainerProps {
  paymentConnectors: IPaymentConnector[];
  mainContentClass?: string;
  cartId: string;
  orderDetails: ISalesOrderResponse;
  isForterValidationEnabled: boolean;
  synchronyConfiguration: SynchronyConfiguration;
  progressiveConfiguration: ProgressiveConfiguration;
  progressiveConnectorId?: string | null;
  affirmConfiguration: AffirmConfiguration;
  skeletonBlockedCount?: number;
  usaEPayErrorMessages?: Maybe<Maybe<UsAePayErrorMessage>[]> | undefined;
  paymentState?: IPaymentState;
  featureFlags?: Maybe<Maybe<FeatureFlag>[]> | undefined;
  isForterValidationEnabledForAdyen?: boolean;
}

const PaymentContainer: React.FC<PaymentsContainerProps> = ({
  paymentConnectors,
  mainContentClass,
  cartId,
  orderDetails,
  isForterValidationEnabled,
  synchronyConfiguration,
  progressiveConfiguration,
  affirmConfiguration,
  skeletonBlockedCount,
  usaEPayErrorMessages,
  featureFlags,
  paymentState = {
    isBelowMinimumAmount: false,
    isOtherAmountSelected: false,
    isBalanceSelected: true,
    hasPartialPayment: false,
    isStoreBlockingSynchrony: false,
    isStoreBlockingProgressive: false,
    isStoreExcludedFromFinancing: false,
    isStoreBlockingAffirm: false,
  },
  isForterValidationEnabledForAdyen,
}) => {
  const billingAddressState = useBillingAddressState();
  const isLoading = useIsLoading();
  const setSelectedPayment = useSetSelectedPaymentOption();
  const selectedPayment = useSelectedPaymentOption();
  const setPaymentReview = useSetPaymentReview();
  const setProgressiveDetails = useSetProgressiveDetails();
  const setPaymentConnectorId = useSetPaymentConnectorId();
  const setCartId = useSetCartId();

  useEffect(() => {
    setCartId(cartId);
  }, [cartId, setCartId]);

  const hasBillingAddressErrors = isBillingFormInValid(billingAddressState);
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    const selectedConnector = paymentConnectors.find(
      (connector) => connector.tenderType === value,
    );
    setPaymentConnectorId(
      selectedConnector?.connectorHtml?.PaymentConnectorId || '',
    );
    setSelectedPayment(value);
    ({
      [TYPES.CREDIT_CARD]: handleCreditCardRadioTealium,
      [TYPES.SYNCHRONY]: handleSynchronyRadioTealium,
      [TYPES.AFFIRM]: handleAffirmRadioTealium,
      [TYPES.PROGRESIVE]: handleProgressiveRadioTealium,
      [TYPES.ADYEN]: handleAdyenRadioTealium,
    })[value]?.();
  };
  const progressiveOrderReview = (pricingInfo: IGetPricing) => {
    setPaymentReview({
      review: true,
    });
    setProgressiveDetails(pricingInfo);
  };
  const additionalAmount = formatCurrency(
    MINIMUM_FINANCING_AMOUNT - orderDetails.AmountDue,
  );
  const { title, description } = paymentState.isOtherAmountSelected
    ? getBalanceErrorMessages().otherAmountSelected
    : getBalanceErrorMessages(additionalAmount).belowMinimumAmount;

  const syfOverlay = useSyfOverlay();
  const onlyCreditCardInConfig = paymentConnectors.some(
    (connector) =>
      (connector.tenderType
        ?.toLowerCase()
        .includes(TYPES.CREDIT_CARD.toLowerCase()) ||
        connector.tenderType
          ?.toLowerCase()
          .includes(TYPES.ADYEN.toLowerCase())) &&
      paymentConnectors.length === 1,
  );

  const prevActiveRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const preventTabFocus = preventTab({
      syfOverlay,
      backdropSelector: `.${Styles.backdrop}`,
      prevActiveRef,
    });

    return preventTabFocus;
  }, [syfOverlay]);

  return (
    <section className={mainContentClass}>
      <h4 tabIndex={0}>Payment Type</h4>
      <div
        className={`${Styles.paymentContainer}
        ${isLoading && Styles.fixedHeight}
         ${
           (paymentState.hasPartialPayment ||
             (paymentState.isStoreBlockingProgressive &&
               paymentState.isStoreBlockingSynchrony &&
               paymentState.isStoreBlockingAffirm) ||
             paymentState.isStoreExcludedFromFinancing) &&
           Styles.ccOnly
         }
        ${Styles.count}${skeletonBlockedCount}
        ${
          paymentState.isStoreBlockingAffirm ||
          paymentState.isStoreBlockingProgressive ||
          paymentState.isStoreBlockingSynchrony
            ? Styles.affimHidden
            : ''
        }
        `}
      >
        <PaymentSkeletonParent
          loading={isLoading}
          storeBlockingSynchrony={paymentState.isStoreBlockingSynchrony}
          storeBlockingProgressive={paymentState.isStoreBlockingProgressive}
          isStoreExcludedFromFinancing={
            paymentState.isStoreExcludedFromFinancing
          }
          storeBlockingAffirm={paymentState.isStoreBlockingAffirm}
        />

        <div
          className={Styles.paymentConnector}
          style={{
            opacity: !isLoading ? 1 : 0,
            visibility: !isLoading ? 'visible' : 'hidden',
            transition: 'opacity 0.2s ease, visibility 0.2s ease',
          }}
        >
          {paymentConnectors.map((acceptContent, index) => {
            const isSynchrony = acceptContent.tenderType === TYPES.SYNCHRONY;
            const isProgressive = acceptContent.tenderType === TYPES.PROGRESIVE;
            const isAffirm = acceptContent.tenderType === TYPES.AFFIRM;
            if (
              (isSynchrony || isProgressive || isAffirm) &&
              paymentState.hasPartialPayment
            )
              return null;
            return (
              <React.Fragment key={acceptContent.tenderType}>
                <PaymentSelector
                  checkoutConfigs={synchronyConfiguration}
                  checkoutCart={orderDetails}
                  affirmConfigs={affirmConfiguration}
                  progressiveConfigs={progressiveConfiguration}
                  isDisabled={
                    (isSynchrony || isProgressive || isAffirm) &&
                    (paymentState.isOtherAmountSelected ||
                      paymentState.isBelowMinimumAmount ||
                      !paymentState.isBalanceSelected ||
                      hasBillingAddressErrors)
                  }
                  isSingleLine={
                    (isSynchrony || isProgressive || isAffirm) &&
                    paymentState.isBelowMinimumAmount &&
                    paymentState.isBalanceSelected
                  }
                  {...{
                    acceptContent,
                    handleChange,
                    index,
                    selectedPayment,
                  }}
                />
                <div
                  className={`${selectedPayment !== acceptContent.tenderType ? Styles.hide : ''} ${isAffirm && !paymentState.isOtherAmountSelected && !hasBillingAddressErrors && Styles.affirmWrapper}`}
                >
                  {!(
                    (isSynchrony || isProgressive || isAffirm) &&
                    (paymentState.isOtherAmountSelected ||
                      paymentState.isBelowMinimumAmount ||
                      hasBillingAddressErrors)
                  ) && (
                    <PaymentOptions
                      progressiveOrderReview={(pricingInfo) =>
                        progressiveOrderReview(pricingInfo)
                      }
                      checkoutConfigs={synchronyConfiguration}
                      checkoutCart={orderDetails}
                      {...{
                        acceptContent,
                        cartId,
                        isForterValidationEnabled,
                        affirmConfiguration,
                        featureFlags,
                        isForterValidationEnabledForAdyen,
                      }}
                      progressiveConfigs={progressiveConfiguration}
                      usaEPayErrorMessages={usaEPayErrorMessages}
                      paymentState={paymentState}
                    />
                  )}
                </div>
              </React.Fragment>
            );
          })}

          {!paymentState.hasPartialPayment &&
            (paymentState.isBelowMinimumAmount ||
              paymentState.isOtherAmountSelected) &&
            (!paymentState.isStoreBlockingSynchrony ||
              !paymentState.isStoreBlockingProgressive) && (
              <PaymentBalanceError
                title={title}
                description={description}
                className={
                  selectedPayment === TYPES.SYNCHRONY &&
                  paymentState.isOtherAmountSelected
                    ? Styles.synchronyOtherAmount
                    : ''
                }
              />
            )}
          {hasBillingAddressErrors &&
            paymentState.isBalanceSelected &&
            !paymentState.hasPartialPayment &&
            !paymentState.isBelowMinimumAmount &&
            !onlyCreditCardInConfig && (
              <PaymentBalanceError
                title={BILLING_ADDRESS_WARNING.title}
                description={BILLING_ADDRESS_WARNING.description}
                className={Styles.billingAddressWarning}
              />
            )}
        </div>
      </div>
      {syfOverlay && (
        <div className={Styles.backdrop} tabIndex={-1} aria-label="test-syf" />
      )}
    </section>
  );
};

function PaymentSkeletonParent(props: {
  loading: boolean;
  storeBlockingSynchrony: boolean;
  storeBlockingProgressive: boolean;
  isStoreExcludedFromFinancing: boolean;
  storeBlockingAffirm?: boolean;
}) {
  return (
    <div
      className={Styles.loader}
      style={{
        visibility: props.loading ? 'visible' : 'hidden',
      }}
    >
      <div className={Styles.skeletonWrapper}>
        <div className={Styles.radioSkeleton}>
          <CustomSkeleton type="text" height="56" width="100%" />
        </div>
        <div>
          <CustomSkeleton type="text" height="60px" width="100%" />
        </div>
        <div className={Styles.width50}>
          <CustomSkeleton type="text" height="60" />
          <CustomSkeleton type="text" height="60" />
        </div>
        {!props.storeBlockingSynchrony &&
          !props.isStoreExcludedFromFinancing && (
            <div
              className={`${Styles.radioSkeleton} ${Styles.synchronySkeleton}`}
            >
              <CustomSkeleton type="text" height="56" width="100%" />
            </div>
          )}

        {!props.storeBlockingProgressive &&
          !props.isStoreExcludedFromFinancing && (
            <div
              className={`${Styles.radioSkeleton} ${Styles.synchronySkeleton}`}
            >
              <CustomSkeleton type="text" height="56" width="100%" />
            </div>
          )}
        {!props.storeBlockingAffirm && !props.isStoreExcludedFromFinancing && (
          <div
            className={`${Styles.radioSkeleton} ${Styles.synchronySkeleton}`}
          >
            <CustomSkeleton type="text" height="56" width="100%" />
          </div>
        )}
      </div>
    </div>
  );
}

function preventTab(options: {
  syfOverlay: boolean;
  backdropSelector: string;
  prevActiveRef: React.MutableRefObject<HTMLElement | null>;
}) {
  const { syfOverlay, backdropSelector, prevActiveRef } = options;

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Tab' && syfOverlay) {
      e.preventDefault();
    }
  };

  if (syfOverlay) {
    prevActiveRef.current = document.activeElement as HTMLElement | null;

    const backdropEl = document.querySelector(
      backdropSelector,
    ) as HTMLElement | null;
    if (backdropEl) {
      backdropEl.setAttribute('tabindex', '-1');
      backdropEl.focus();
    }

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (
        prevActiveRef.current &&
        typeof prevActiveRef.current.focus === 'function'
      ) {
        prevActiveRef.current.focus();
      }
    };
  }

  return () => {
    document.removeEventListener('keydown', onKeyDown, true);
  };
}

export default PaymentContainer;
